/**
 * What OS Buddy did on its own with one mailbox email (#3739): linked its
 * attachments to a work order, or linked the thread to a customer. Each line
 * can be undone; an undone action stays visible, marked undone.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, CircularProgress, Link, Stack } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import type { EmailAutoAction } from '@/types';

export interface EmailAutoActionUndone {
  actionId: string;
  kind: EmailAutoAction['kind'];
  undoneAt: string;
}

interface EmailAutoActionsProps {
  conversationId: string;
  actions: EmailAutoAction[];
  onUndone?: (undone: EmailAutoActionUndone) => void;
}

function AutoActionLine({
  conversationId,
  action,
  onUndone,
}: {
  conversationId: string;
  action: EmailAutoAction;
  onUndone?: ((undone: EmailAutoActionUndone) => void) | undefined;
}) {
  const { t } = useTranslation('messages');
  const notify = useNotify();
  const canEditWorkOrders = useAuthStore((s) => s.hasPermission('work_orders.update'));
  const [busy, setBusy] = useState(false);
  const undone = action.undoneAt !== null;
  const canUndo = !undone && (action.kind === 'link_customer' || canEditWorkOrders);

  const undo = async () => {
    setBusy(true);
    try {
      const result = await api.post<{ undoneAt: string }>(
        `/conversations/${conversationId}/auto-actions/${action.id}/undo`,
      );
      notify.success(t('emailAutoUndone'));
      onUndone?.({ actionId: action.id, kind: action.kind, undoneAt: result.undoneAt });
    } catch (error) {
      const changed =
        error instanceof ApiError &&
        (error.data as { code?: unknown } | undefined)?.code === 'CUSTOMER_CHANGED';
      notify.error(changed ? t('emailAutoCustomerChanged') : t('emailAutoUndoFailed'));
    } finally {
      setBusy(false);
    }
  };

  let text: ReactNode;
  if (action.kind === 'attach_to_work_order') {
    const number = action.workOrder?.number ?? t('emailAutoWorkOrderUnnumbered');
    const workOrder = action.workOrder ? (
      <Link component={RouterLink} to={`/workorders/${action.workOrder.id}`}>
        {number}
      </Link>
    ) : (
      number
    );
    text = (
      <>
        {undone ? t('emailAutoAttachedUndone') : t('emailAutoAttached', { count: action.files })} {workOrder}
      </>
    );
  } else {
    const name = action.customer?.name ?? t('emailAutoCustomerRemoved');
    text = undone ? t('emailAutoLinkedUndone', { name }) : t('emailAutoLinked', { name });
  }

  return (
    <Alert
      severity={undone ? 'warning' : 'info'}
      variant="outlined"
      icon={<AutoAwesomeIcon fontSize="small" />}
      data-testid={`email-auto-action-${action.kind}`}
      action={
        canUndo ? (
          <Button
            color="inherit"
            size="small"
            onClick={() => { void undo(); }}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : undefined}
          >
            {t('emailAutoUndo')}
          </Button>
        ) : undefined
      }
    >
      {text}
    </Alert>
  );
}

export function EmailAutoActions({ conversationId, actions, onUndone }: EmailAutoActionsProps) {
  return (
    <Stack spacing={1}>
      {actions.map((action) => (
        <AutoActionLine key={action.id} conversationId={conversationId} action={action} onUndone={onUndone} />
      ))}
    </Stack>
  );
}
